import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Tenant, TenantService } from './tenant.service';
import { PlatformService } from '../core/platform/platform.service';
import { StorageService } from '../core/platform/storage.service';

/**
 * Magic Link Request/Response interfaces
 */
interface MagicLinkStartRequest {
  email: string;
  tenant?: string;
}

interface MagicLinkStartResponse {
  detail: string;
  dev_magic_link?: string;
  dev_note?: string;
}

interface MagicLinkConfirmRequest {
  token: string;
  tenant?: string;
}

interface MagicLinkConfirmResponse {
  detail?: string;
  twofa_used?: boolean;
  twofa_required?: boolean;
  mfa_token?: string;
  tokens?: {
    access: string;
    refresh: string;
  };
  access?: string;
  refresh?: string;
  user?: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
  };
}

export interface CurrentUser {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  roles?: string[];
  is_staff?: boolean;
  is_superuser?: boolean;
}

interface TokenRefreshRequest {
  refresh: string;
}

interface TokenRefreshResponse {
  access: string;
  refresh?: string;
}

/**
 * Two-FA interfaces
 */
interface TwoFAChallengeRequest {
  email: string;
  method: 'sms' | 'email' | 'totp';
}

interface TwoFAChallengeResponse {
  challenge_id: string;
  available_methods: string[];
}

interface TwoFAVerifyRequest {
  challenge_id: string;
  code: string;
}

interface TwoFAVerifyResponse {
  access: string;
  refresh: string;
  user: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly apiPath = '/api/auth';
  
  // Observable for tracking authentication state
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.hasValidToken());
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  // Observable for current user
  private currentUserSubject = new BehaviorSubject<CurrentUser | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private http: HttpClient,
    private tenantService: TenantService,
    private platformService: PlatformService,
    private storageService: StorageService
  ) {
    this.loadStoredUser();
  }

  private getApiUrl(): string {
    return `${this.tenantService.getApiBaseUrl()}${this.apiPath}`;
  }

  /**
   * Start magic link authentication flow
   * @param email User email
   * @param tenant Optional tenant schema name
   */
  startMagicLink(email: string, tenant?: string): Observable<MagicLinkStartResponse> {
    this.prepareTenantForAuthFlow(tenant);

    const request: MagicLinkStartRequest = {
      email,
      ...(tenant && { tenant })
    };

    return this.http.post<MagicLinkStartResponse>(
      `${this.getApiUrl()}/magic/start`,
      request
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Confirm magic link and get JWT tokens
   * @param token Magic link token from URL
   * @param tenant Optional tenant schema name
   */
  confirmMagicLink(token: string, tenant?: string): Observable<MagicLinkConfirmResponse> {
    this.prepareTenantForAuthFlow(tenant);

    const request: MagicLinkConfirmRequest = {
      token,
      ...(tenant && { tenant })
    };

    return this.http.post<MagicLinkConfirmResponse>(
      `${this.getApiUrl()}/magic/confirm`,
      request
    ).pipe(
      tap(response => this.handleAuthResponse(response)),
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Refresh access token using refresh token
   */
  refreshToken(refreshToken: string): Observable<TokenRefreshResponse> {
    const request: TokenRefreshRequest = { refresh: refreshToken };

    return this.http.post<TokenRefreshResponse>(
      `${this.getApiUrl()}/token/refresh`,
      request
    ).pipe(
      tap(response => {
        this.setAccessToken(response.access);
        if (response.refresh) {
          this.setRefreshToken(response.refresh);
        }
      }),
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Start two-factor authentication challenge
   * @param email User email
   * @param method SMS, email, or TOTP
   */
  startTwoFAChallenge(email: string, method: 'sms' | 'email' | 'totp'): Observable<TwoFAChallengeResponse> {
    const request: TwoFAChallengeRequest = { email, method };

    return this.http.post<TwoFAChallengeResponse>(
      `${this.getApiUrl()}/two-fa/challenge/`,
      request
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Verify two-factor authentication code
   * @param challengeId Challenge ID from challenge request
   * @param code Verification code (from SMS, email, or TOTP)
   */
  verifyTwoFA(challengeId: string, code: string): Observable<TwoFAVerifyResponse> {
    const request: TwoFAVerifyRequest = { challenge_id: challengeId, code };

    return this.http.post<TwoFAVerifyResponse>(
      `${this.getApiUrl()}/two-fa/verify/`,
      request
    ).pipe(
      tap(response => this.handleAuthResponse(response)),
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Logout and clear tokens
   */
  logout(): void {
    this.clearAuthData();
    this.tenantService.setCurrentTenantSchema(null);
    this.isAuthenticatedSubject.next(false);
    this.currentUserSubject.next(null);
  }

  private prepareTenantForAuthFlow(tenant?: string): void {
    if (!this.platformService.isBrowser()) {
      return;
    }

    const normalizedTenant = tenant?.trim() || null;
    this.tenantService.setCurrentTenantSchema(normalizedTenant);
  }

  /**
   * Get current access token from storage
   */
  getAccessToken(): string | null {
    if (!this.platformService.isBrowser()) {
      return null;
    }
    return this.storageService.getLocalItem('access_token');
  }

  /**
   * Get current refresh token from storage
   */
  getRefreshToken(): string | null {
    if (!this.platformService.isBrowser()) {
      return null;
    }
    return this.storageService.getLocalItem('refresh_token');
  }

  /**
   * Check if user is authenticated (has valid token)
   */
  isAuthenticated(): boolean {
    return this.hasValidToken();
  }

  /**
   * Handle successful authentication response
   * Store tokens and user data
   */
  private handleAuthResponse(response: any): void {
    const access = response?.access ?? response?.tokens?.access ?? null;
    const refresh = response?.refresh ?? response?.tokens?.refresh ?? null;

    if (access) {
      this.setAccessToken(access);
    }
    if (refresh) {
      this.setRefreshToken(refresh);
    }
    if (response.user) {
      this.setCurrentUser(response.user);
    }

    if (access) {
      this.isAuthenticatedSubject.next(true);
      this.ensureCurrentUserLoaded();
    }
  }

  /**
   * Set access token in storage
   */
  private setAccessToken(token: string): void {
    if (!this.platformService.isBrowser()) {
      return;
    }
    this.storageService.setLocalItem('access_token', token);
  }

  /**
   * Set refresh token in storage
   */
  private setRefreshToken(token: string): void {
    if (!this.platformService.isBrowser()) {
      return;
    }
    this.storageService.setLocalItem('refresh_token', token);
  }

  /**
   * Set current user data
   */
  private setCurrentUser(user: CurrentUser): void {
    if (!this.platformService.isBrowser()) {
      return;
    }
    this.storageService.setLocalItem('current_user', JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  /**
   * Fetch current user profile from API
   */
  fetchCurrentUser(): Observable<CurrentUser> {
    return this.http.get<CurrentUser>(`${this.getApiUrl()}/me`).pipe(
      tap(user => this.setCurrentUser(user)),
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Ensure current user is loaded when authenticated
   */
  ensureCurrentUserLoaded(): void {
    if (!this.platformService.isBrowser()) {
      return;
    }
    if (!this.hasValidToken()) {
      return;
    }
    if (this.currentUserSubject.value) {
      return;
    }

    this.fetchCurrentUser().subscribe({
      next: () => undefined,
      error: () => undefined
    });
  }

  /**
   * Load stored user from localStorage
   */
  private loadStoredUser(): void {
    if (!this.platformService.isBrowser()) {
      return;
    }
    const userData = this.storageService.getLocalItem('current_user');
    if (userData) {
      try {
        this.currentUserSubject.next(JSON.parse(userData));
        if (this.hasValidToken()) {
          this.fetchCurrentUser().subscribe({
            next: () => undefined,
            error: () => undefined
          });
        }
      } catch (e) {
        console.error('Failed to parse stored user data', e);
      }
    } else {
      this.ensureCurrentUserLoaded();
    }
  }

  /**
   * Clear all authentication data
   */
  private clearAuthData(): void {
    if (!this.platformService.isBrowser()) {
      return;
    }
    this.storageService.removeLocalItem('access_token');
    this.storageService.removeLocalItem('refresh_token');
    this.storageService.removeLocalItem('current_user');
  }

  /**
   * Check if stored token is still valid (basic check)
   */
  private hasValidToken(): boolean {
    if (!this.platformService.isBrowser()) {
      return false;
    }
    const token = this.storageService.getLocalItem('access_token');
    if (!token) {
      return false;
    }

    // Decode JWT and check expiration
    try {
      const payload = this.parseJwt(token);
      if (payload.exp) {
        return payload.exp * 1000 > Date.now();
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Parse JWT token payload
   */
  private parseJwt(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        this.platformService.decodeBase64(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Failed to parse JWT', e);
      return {};
    }
  }

  /**
   * Get list of available tenants (clubs) for public login page
   * PUBLIC endpoint - no authentication required
   */
  getAvailableTenants(): Observable<Tenant[]> {
    const publicApiBaseUrl = (environment as { publicApiUrl?: string }).publicApiUrl ?? environment.apiUrl;
    return this.http.get<Tenant[]>(`${publicApiBaseUrl}/api/v1/public/tenants/`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'An error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = error.error.message;
    } else {
      // Server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }

    return throwError(() => error);
  }
}
